/**
 * Capas SVG del mapa de sanidad (T-A). Todas puras y memoizadas: reciben el
 * modelo derivado y NUNCA el estado de hover (el resaltado va en un overlay
 * aparte del contenedor). El hit-testing es centralizado (hitTestModel) — las
 * capas no llevan listeners: con ~600 líneas × modos, un listener por path
 * mataría el rendimiento móvil.
 *
 * Rediseño 2026-08-07 (critique impeccable, 16/40 → figura/fondo + unidades):
 *  1. FIGURA/FONDO. El lienzo era `slate-50` y la nave `fill="white"`: 1.06:1
 *     de separación, invisible a pleno sol. Ahora el suelo es tierra con
 *     retícula métrica y la nave es una superficie clara con borde de tinta y
 *     sombra proyectada — flota sobre el terreno (~2:1 de figura/fondo, 4.8:1
 *     en el borde).
 *  2. UNIDADES. Todo se dibujaba en METROS, así que a zoom de finca las líneas
 *     salían a 0.44px (moaré) y al máximo zoom el desenfoque medía 93px
 *     (niebla). Ahora los trazos van con `vectorEffect="non-scaling-stroke"` y
 *     los textos/pines con `mpp` (metros por píxel): constantes en pantalla a
 *     cualquier zoom, con nivel de detalle por densidad.
 *  3. CALOR. Se componía dos veces (grupo 0.85 × trazo 0.35→1) y encima pasaba
 *     por un blur: el rojo de brote grave llegaba al ojo a 1.60:1 en su presión
 *     mínima — el foco nuevo y aislado, el más decisivo, era el menos visible.
 *     Ahora son DOS señales: un campo de presión suave (la zona, recortado al
 *     perímetro y por DEBAJO de las líneas) y un tramo nítido y opaco que
 *     SUSTITUYE a la línea de cultivo (el dato, siempre a color pleno; la
 *     presión modula el grosor, no la opacidad).
 */
import { memo, useMemo } from "react";
import type {
  FarmHealthModel,
  GhModel,
  GhRow,
  LineState,
} from "@/hooks/useFarmHealthMap";
import { focusWeight } from "@/lib/health/heatSegments";
import { focusOpacity, worseState, TREATED_BLOCK_DAYS } from "@/lib/health/farmHealthModel";
import {
  layoutFoci,
  layoutTreatments,
  pickFocusAt,
  pickTreatmentAt,
} from "@/lib/health/fociLayout";
import { distanceToRing, nearestPointOnSegment, type XY } from "@/lib/geo/wgs84Local";
import { rotatedPlaneBox, toLocal } from "@/lib/health/ghTransform";

/**
 * Paleta del CAMPO de presión (manchas suaves) y de los puntos de leyenda.
 *
 * UN SOLO EJE: en qué punto de su vida está el parte. Antes había seis tonos
 * para cinco estados y dos de ellos —rojo contra naranja— no decían el estado
 * sino la GRAVEDAD, así que el color contestaba a dos preguntas distintas
 * según cuál mirases. La gravedad se fue al tamaño del pin (`FociMarkers`).
 *
 *   rojo    sin tratar, nadie ha hecho nada
 *   azul    tratamiento previsto, sin ejecutar
 *   verde   tratado (o el trabajador lo da por hecho, a falta de validar)
 *   gris    resuelto y validado — deja de pedir atención
 */
export const LINE_STATE_COLORS: Record<LineState, string> = {
  open: "hsl(0 72% 51%)",
  planned: "hsl(210 80% 52%)",
  treated: "hsl(145 58% 42%)",
  /* VERDE APAGADO, no el gris-azulado de antes (petición del usuario
     31-08-2026: «que cambie de rojo a otra cosa, potencialmente verde»).
     Antes tiraba a teal y se leía como «sin datos» en vez de como «esto acabó
     bien». Ahora está claramente en la familia del verde, pero desaturado y a
     media opacidad: se distingue de `treated` —que es verde vivo y SÍ pide
     algo, validarlo— sin dejar de leerse como cerrado. */
  resolved: "hsl(150 25% 60%)",
  no_data: "hsl(150 10% 78%)",
};

/**
 * Paleta de TINTA para marcas nítidas (tramos calientes, pines, anillos). La
 * de arriba se diseñó para manchas translúcidas y sobre el suelo claro de la
 * nave no llegaba: el ámbar daba 1.79:1 y el naranja 2.90:1 contra un mínimo
 * exigible de 3:1. Estos tonos son los mismos estados, oscurecidos hasta que
 * todos pasan (5.98 / 4.29 / 3.60 / 5.52 / 4.59 : 1 sobre el suelo de nave).
 */
export const LINE_STATE_INK: Record<LineState, string> = {
  open: "hsl(0 74% 44%)",
  planned: "hsl(212 78% 43%)",
  treated: "hsl(147 62% 29%)",
  resolved: "hsl(152 26% 40%)",
  no_data: "hsl(150 8% 55%)",
};

/**
 * GESTIÓN DE RESISTENCIAS: su propia pareja de tonos.
 *
 * La cápsula de IRM tomaba prestados `outbreak_high` y `reported` de la paleta
 * de estados. Eran el rojo y el ámbar de siempre, así que se veía bien, pero
 * ataba dos cosas que no tienen nada que ver: el día que la paleta de estados
 * cambiara —hoy— el ámbar dejaba de existir y la cápsula se quedaba sin color
 * sin que nadie lo hubiera decidido. Son los mismos valores de antes, ahora
 * dichos aquí.
 */
const IRM_INK = { warning: "hsl(38 92% 38%)", over: "hsl(0 74% 44%)" } as const;

// ── Materia del mundo ────────────────────────────────────────────────────────
// Tierra PLANA y clara (QA usuario 2026-08-07): el degradado se veía como dos
// marrones distintos según dónde cayeran las naves. Al aclararla, la figura la
// sostienen el borde de la nave (6.5:1) y su sombra, no el salto de tono.
/**
 * El SUELO del mapa. Pasa de tierra a hierba (petición del usuario 25-08-2026):
 * lo que rodea a un invernadero es campo, no un solar.
 *
 * Verde claro, elegido por el usuario sobre una muestra (25-08-2026). Los dos
 * intentos anteriores fueron míos y ninguno era esto: uno tan lavado que se veía
 * gris y otro ya hierba de campo.
 *
 * Con un suelo tan claro, la nave —que es casi blanca— NO se separa por el salto
 * de tono: 1,26:1, y por debajo de 1,5 el ojo no lo llama borde. Quien sostiene
 * la figura es el borde de la nave (`GH_EDGE`, 6,5:1) y su sombra, que es
 * exactamente el reparto que ya se decidió el 07-08-2026 al aplanar el suelo. Si
 * alguna vez se quita ese borde, esto se vuelve invisible a pleno sol.
 *
 * Las manchas de presión (naranjas y rojas) siguen destacando: son lo único
 * saturado de la pantalla.
 */
/* Blanco liso en todos los dispositivos (petición del usuario 26-08-2026).
   Era un verde pálido de campo. La constante se toca UNA vez y se lleva con ella
   el fondo del contenedor, el del lienzo, el rectángulo de suelo del SVG y el
   degradado de la cabecera, que salen todos de aquí. */
export const SOIL = "#FFFFFF";
/** Tinta sobre la hierba: lo que se escriba encima del mapa se lee de aquí. */
export const GROUND_INK = "hsl(100 30% 15%)";
/** La misma, en segundo plano (subtítulos, unidades). */
export const GROUND_INK_SOFT = "hsl(100 16% 30%)";

/**
 * VIDRIO SOBRE EL MAPA: el aspecto de todo lo que flota encima —estadísticas,
 * sensores, el periodo y la leyenda de presión—.
 *
 * Tinta a muy baja opacidad: se ve el mapa a través y aun así se lee lo de
 * dentro. Una sola definición para los cuatro a propósito; antes unos eran
 * pastillas blancas y otro una tinta oscura, y encima de una foto —que es lo
 * que es un mapa— lo blanco se lee como un parche pegado.
 *
 * SIN `backdrop-blur`. Lo llevaba, y desenfocar el fondo es de las cosas más
 * caras que hay: el navegador tiene que releer y desenfocar lo que hay DETRÁS
 * en cada fotograma, y detrás hay un mapa que se está moviendo. Ocho mandos
 * flotando = ocho desenfoques a pantalla completa por fotograma. Y desde que el
 * suelo es blanco liso no desenfocaba nada: la pastilla se ve exactamente igual
 * sin él.
 */
export const MAP_GLASS_CLASS =
  "bg-white ring-1 ring-[hsl(210_25%_86%)] shadow-[0_2px_10px_rgba(15,39,68,0.18)]";

/**
 * La tinta de todo lo que flota encima del mapa. Negra de verdad sobre blanco
 * macizo (petición del usuario 26-08-2026).
 *
 * Antes era una tinta verde muy oscura sobre un velo del 10 %, que sobre el
 * suelo blanco del plano abstracto se leía perfectamente… y sobre una FOTO DE
 * SATÉLITE desaparecía: el velo dejaba pasar tejados, tierra y carreteras, y el
 * botón se convertía en un fantasma. Con imagen detrás no hay translucidez que
 * valga — el fondo cambia de un metro al siguiente—, así que los mandos son
 * blancos y opacos.
 */
export const MAP_CHROME_INK = "#13314A";

/**
 * VIDRIO OSCURO, al estilo de los mandos de Mapas y Buscar de iOS (petición del
 * usuario 26-08-2026): gris muy oscuro translúcido, filo claro de un píxel y
 * sombra baja. Es el aspecto de los mandos del TELÉFONO Y LA TABLETA —la pila
 * de la derecha y la barra de abajo—, donde el mapa ocupa la pantalla entera y
 * una fila de pastillas blancas se lee como un papel pegado encima de la foto.
 *
 * SIN `backdrop-blur`, igual que el claro y por el mismo motivo: desenfocar lo
 * de detrás obliga al navegador a releer la pantalla en cada fotograma, y
 * detrás hay una foto de satélite moviéndose bajo el dedo. Es exactamente el
 * gasto que se le quitó a este mapa en agosto de 2026 y que el usuario notó en
 * un iPhone nuevo.
 *
 * Lo que hace que se lea igual sin el desenfoque es la OPACIDAD: 0,72 es
 * bastante más opaco que el vidrio de Apple, precisamente porque no hay
 * desenfoque que homogeneice lo de detrás. Con menos, un tejado blanco por
 * debajo se come el icono.
 */
export const MAP_GLASS_DARK_CLASS =
  "bg-[rgba(34,36,40,0.72)] ring-1 ring-white/15 shadow-[0_4px_16px_rgba(0,0,0,0.35)]";
/**
 * LA HOJA de detalle sobre el mapa (el sensor abierto), en teléfono y tableta.
 *
 * Es el mismo negro que los mandos pero CASI OPACO, y a propósito: un mando es
 * una pastilla pequeña y lo que se ve por detrás es paisaje; una hoja ocupa
 * media pantalla y por detrás pasa el plano entero —líneas, sectores, rótulos—,
 * que se cuela entre las cifras y las vuelve ilegibles (petición del usuario
 * 26-08-2026). Queda un 6 % de transparencia, lo justo para que se note que hay
 * mapa debajo y no una pantalla nueva.
 *
 * La sombra va hacia ARRIBA: es lo que la despega del mapa por su único borde
 * visible.
 */
export const MAP_SHEET_DARK_CLASS =
  "bg-[rgba(20,21,24,0.94)] ring-1 ring-white/10 shadow-[0_-10px_36px_rgba(0,0,0,0.45)]";

/** La tinta de lo que va sobre el vidrio oscuro. */
export const MAP_CHROME_INK_DARK = "#FFFFFF";
/** El activo, sobre vidrio oscuro: el azul de la app aclarado para que aguante
 *  el contraste contra un fondo oscuro (el de la app se apaga ahí). */
export const MAP_CHROME_ACTIVE_DARK = "#5AA9FF";
/* La retícula deja de ser verde con el suelo: unas líneas verdes sobre blanco
   se leen como un resto del diseño anterior, no como una referencia métrica.
   Gris neutro y flojo — sigue estando (la barra de escala se apoya en ella para
   que el plano se pueda medir a ojo), pero no compite con nada. */
const SOIL_GRID = "hsl(210 16% 82%)";
const GH_FLOOR = "hsl(200 40% 99%)"; // suelo de la nave: plástico iluminado
const GH_EDGE = "hsl(207 37% 28%)"; // borde de la nave (tinta-2 del sistema)
const ROW_GREEN = "hsl(150 28% 48%)"; // línea de cultivo: contexto, no dato (3.16:1)
const BLOCK_EDGE = "hsl(207 24% 46%)"; // separación entre bloques (4.54:1)
const BLOCK_TINT = "hsl(203 24% 95.5%)"; // bloques alternos: banda apenas perceptible
/**
 * EL SECTOR PULVERIZADO, EN VERDE.
 *
 * Una pulverización no ocurre en un punto: ocurre en el sector entero. Ponerla
 * como chincheta obligaba a inventarle un sitio —el centroide—, que además es
 * donde caen los partes viejos sin línea, así que las dos cosas se tapaban.
 * El área es el canal que le corresponde.
 *
 * Se APAGA con los días, porque «tratado hace tres semanas» no es lo mismo que
 * «tratado ayer», y a los 21 días desaparece: la misma ventana con la que un
 * tratamiento puede seguir hablando de un parte.
 *
 * Y NO dice que ningún parte esté resuelto. Los partes abiertos siguen rojos
 * encima —eso es lo que el usuario llamó «los colores mezclándose»: sector
 * verde con focos rojos = «aquí se trató, y aun así esto sigue»—.
 */
const TREATED_BLOCK_TINT = "hsl(145 52% 55%)";
/** Recién tratado / al filo de la ventana. Nunca llega a tapar la foto. */
const TREATED_TINT_MAX = 0.3;
const TREATED_TINT_MIN = 0.08;
const INK = "hsl(207 57% 18%)"; // tinta del sistema, para etiquetas
const PHI_INK = "hsl(263 68% 52%)"; // plazo de seguridad

// ── Nivel de detalle ─────────────────────────────────────────────────────────
// `mpp` = metros que mide un píxel. Cuanto MAYOR, más lejos estás.
const LOD_ROWS_FADE = 0.62; // por encima, las líneas se disuelven en textura
const LOD_ROWS_FULL = 0.40; // por debajo, líneas a opacidad plena
// Los bloques son ESTRUCTURA, no detalle: se ven en la vista por defecto (que
// ronda 0.5 m/px) y solo desaparecen en fincas enormes. El umbral anterior era
// 0.38 y los ocultaba justo donde más falta hacían (QA usuario 2026-08-07).
const LOD_BLOCK_EDGES = 1.2;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const pathOf = (pts: XY[]) => pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ") + " Z";

/** Caja de un bloque en metros: sirve para decidir, bloque a bloque, si su
 *  nombre cabe en pantalla. Un umbral global de zoom no sabe que en la misma
 *  nave conviven bloques grandes y recortes de dos líneas. */
function blockBox(points: XY[]) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
}

const median = (v: number[]) => {
  const s = [...v].sort((a, b) => a - b);
  return s.length % 2 ? s[(s.length - 1) / 2] : (s[s.length / 2 - 1] + s[s.length / 2]) / 2;
};

/**
 * Dónde va el nombre de un bloque. El centroide, a secas, se pasea: en bandas
 * irregulares o en L cada nombre cae en un sitio distinto y el conjunto se lee
 * desordenado (QA usuario 2026-08-07).
 *
 * Criterio: todos los nombres de una nave comparten CARRIL — una x común si
 * los bloques se apilan en bandas horizontales, una y común si van en
 * columnas. Después, cada nombre se empuja hacia dentro de su propio bloque:
 * se recorta al ancho disponible, se mete en la caja y se comprueba que sus
 * dos extremos caen DENTRO del polígono. Si no caben, se prueban posiciones a
 * lo largo del carril y gana la más cercana al eje común. Si ninguna vale, el
 * bloque se queda sin nombre antes que sacarlo fuera.
 */
function placeBlockLabel(
  points: XY[],
  centroid: XY,
  lane: number,
  stacked: boolean,
  halfW: number,
): XY | null {
  const bb = blockBox(points);
  const pad = 1.5 * halfW * 0.1 + halfW * 0.15;
  const fits = (p: XY) =>
    pointInPolygon(p, points) &&
    pointInPolygon({ x: p.x - halfW, y: p.y }, points) &&
    pointInPolygon({ x: p.x + halfW, y: p.y }, points);

  const lo = bb.minX + halfW + pad;
  const hi = bb.maxX - halfW - pad;
  const clampX = (x: number) => (lo > hi ? (bb.minX + bb.maxX) / 2 : Math.min(hi, Math.max(lo, x)));

  const primary: XY = stacked
    ? { x: clampX(lane), y: centroid.y }
    : { x: clampX(centroid.x), y: lane };
  if (fits(primary)) return primary;

  const fallback: XY = { x: clampX(centroid.x), y: centroid.y };
  if (fits(fallback)) return fallback;

  // Barrido a lo largo del carril: gana la posición válida más cercana al eje.
  let best: XY | null = null;
  let bestD = Infinity;
  for (let i = 0; i <= 8; i++) {
    const t = i / 8;
    const cand: XY = stacked
      ? { x: clampX(bb.minX + t * bb.w), y: primary.y }
      : { x: primary.x, y: bb.minY + t * bb.h };
    if (!fits(cand)) continue;
    const d = stacked ? Math.abs(cand.x - lane) : Math.abs(cand.y - lane);
    if (d < bestD) { bestD = d; best = cand; }
  }
  return best;
}

/** Paso de retícula "redondo" que caiga entre ~44 y ~130px en pantalla. */
export function gridStepMeters(mpp: number): number {
  const steps = [1, 2, 5, 10, 25, 50, 100, 250, 500, 1000];
  const target = 78 * mpp;
  return steps.find((s) => s >= target) ?? 1000;
}

// ── Hit-testing centralizado ─────────────────────────────────────────────────
/* Ya NO existe el objetivo «línea» (29-08-2026). Ver `hitTestModel`. */
export type HoverTarget =
  | { type: "block"; ghId: string; blockId: string }
  | { type: "gh"; ghId: string }
  | { type: "focus"; ghId: string; focusId: string }
  | { type: "treatment"; ghId: string; treatmentId: string };

function pointInPolygon(p: XY, poly: XY[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j];
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}

/** world = punto en el plano común; tolM = tolerancia en metros (según zoom). */
/**
 * Solo los FOCOS, y con su propia tolerancia.
 *
 * El pin se dibuja a tamaño constante EN PANTALLA (via `mpp`), pero el toque se
 * comprobaba contra un radio fijo EN METROS. En una finca de 51 ha, 2,5 m son
 * dos píxeles: el pin se veía enorme y solo respondía si le acertabas al centro
 * exacto — de ahí que unas veces abriera y otras no. Quien llama pasa aquí un
 * radio calculado desde `mpp`, así que la zona sensible mide siempre lo mismo
 * que el dibujo, esté el mapa donde esté de zoom.
 */
export function hitTestFocus(
  model: FarmHealthModel,
  world: XY,
  tolM: number,
  /** Metros por píxel: hace falta para deshacer la baraja (ver `fociLayout`). */
  mpp: number,
): HoverTarget | null {
  let best: { ghId: string; focusId: string; d: number } | null = null;
  for (const gh of model.greenhouses) {
    const local = toLocal(gh, world);
    const placed = layoutFoci(model.foci.filter((f) => f.ghId === gh.id), mpp);
    const hit = pickFocusAt(placed, local, tolM);
    if (!hit) continue;
    const d = Math.hypot(hit.x - local.x, hit.y - local.y);
    if (!best || d < best.d) best = { ghId: gh.id, focusId: hit.focus.id, d };
  }
  return best ? { type: "focus", ghId: best.ghId, focusId: best.focusId } : null;
}

/** Igual que `hitTestFocus`, para las chinchetas de pulverización. */
export function hitTestTreatment(
  model: FarmHealthModel,
  world: XY,
  tolM: number,
  mpp: number,
): HoverTarget | null {
  let best: { ghId: string; treatmentId: string; d: number } | null = null;
  for (const gh of model.greenhouses) {
    const local = toLocal(gh, world);
    const placed = layoutTreatments((model.treatments ?? []).filter((t) => t.ghId === gh.id), mpp);
    const hit = pickTreatmentAt(placed, local, tolM);
    if (!hit) continue;
    const d = Math.hypot(hit.x - local.x, hit.y - local.y);
    if (!best || d < best.d) best = { ghId: gh.id, treatmentId: hit.treatment.id, d };
  }
  return best ? { type: "treatment", ghId: best.ghId, treatmentId: best.treatmentId } : null;
}

export function hitTestModel(
  model: FarmHealthModel,
  world: XY,
  tolM: number,
  /** Metros por píxel: hace falta para deshacer la baraja (ver `fociLayout`). */
  mpp: number,
): HoverTarget | null {
  for (const gh of model.greenhouses) {
    const local = toLocal(gh, world);
    const inBbox =
      local.x >= gh.bbox.minX - tolM && local.x <= gh.bbox.maxX + tolM &&
      local.y >= gh.bbox.minY - tolM && local.y <= gh.bbox.maxY + tolM;
    if (!inBbox) continue;

    // 1º focos (marcas pequeñas, prioridad de toque)
    const placedFoci = layoutFoci(model.foci.filter((f) => f.ghId === gh.id), mpp);
    const focusHit = pickFocusAt(placedFoci, local, Math.max(tolM, 2.5));
    if (focusHit) return { type: "focus", ghId: gh.id, focusId: focusHit.focus.id };

    /* 2º los tratamientos. DESPUÉS de los partes a propósito: las dos barajas
       salen del mismo centroide y en el solape gana lo que está sin resolver. */
    const placedTr = layoutTreatments((model.treatments ?? []).filter((t) => t.ghId === gh.id), mpp);
    const trHit = pickTreatmentAt(placedTr, local, Math.max(tolM, 2.5));
    if (trHit) return { type: "treatment", ghId: gh.id, treatmentId: trHit.treatment.id };

    /* FUERA del perímetro: como mucho, la nave. Nunca un sector.
       Medido en producción: los 36 sectores dibujables TOCAN el perímetro de su
       nave (retranqueo máximo 0,00 m) y en cuatro naves el polígono del sector
       ES el perímetro. Si la tolerancia de sector se aplicara aquí, tocar el
       campo por fuera de la pared entraría en un sector — el 100 % de los puntos
       muestreados a 2 m por fuera. */
    if (!pointInPolygon(local, gh.perimeter)) {
      if (distanceToRing(local, gh.perimeter) <= tolM) return { type: "gh", ghId: gh.id };
      continue;
    }

    // 2º el sector que CONTIENE el punto. La contención va primero a propósito.
    let inside: string | null = null;
    for (const b of gh.blocks) {
      if (pointInPolygon(local, b.points)) { inside = b.id; break; }
    }

    if (inside) {
      /* 3º EL SECTOR, y nada más fino (29-08-2026, petición del dueño).
         Aquí se probaba antes la LÍNEA: si dentro del sector había alguna con
         parte de plaga a menos de la tolerancia, el toque devolvía esa línea.

         Por qué se quita, y no es una cuestión de gusto: la tolerancia es de 3
         METROS y la separación real entre líneas es de 1,5 a 3. O sea que el
         radio de búsqueda es MÁS ANCHO que el hueco entre una línea y la de al
         lado. Tocando una línea vacía —sin ningún parte— el dedo caía dentro de
         los 3 m de la vecina que sí tenía uno, y se abría la ficha de ESA. El
         dueño lo describió exactamente así: «puedes hacer clic en una línea que
         no tiene nada». No era una impresión suya; el radio no distingue.

         Lo que informa de una plaga es su PUNTO, que se dibuja encima de la
         línea que toca y se sigue tocando aparte —`hitTestFocus`, con un radio
         medido en píxeles del tamaño de un dedo, y probado ANTES que esto—.
         Los sensores igual, con su propio manejador. Las líneas se quedan como
         lo que son: dibujo. */
      return { type: "block", ghId: gh.id, blockId: inside };
    }

    /* 4º dentro de la nave pero fuera de todo sector (pasillos, cabeceras): el
       sector MÁS CERCANO dentro de la tolerancia. Esto es lo que antes daba, de
       rebote, el bucle de líneas: `pointInPolygon` no tiene tolerancia ninguna,
       así que sin esto un dedo a 2 cm del borde de un sector caía a la nave. */
    let near: { blockId: string; d: number } | null = null;
    for (const b of gh.blocks) {
      const d = distanceToRing(local, b.points);
      if (d <= tolM && (!near || d < near.d)) near = { blockId: b.id, d };
    }
    if (near) return { type: "block", ghId: gh.id, blockId: near.blockId };

    // 5º la nave.
    return { type: "gh", ghId: gh.id };
  }
  return null;
}

// ── Defs: materia del suelo, sombra de nave, difuminado del campo ────────────
export const MapDefs = memo(function MapDefs({ mpp }: { mpp: number }) {
  const step = gridStepMeters(mpp);
  return (
    <defs>
      {/* Manchas de calor: núcleo caliente (tinta) que se abre al tono vivo y
          se disuelve. Una por estado — el foco solo elige cuál usa. */}
      {(Object.keys(LINE_STATE_INK) as LineState[]).map((s) => (
        <radialGradient key={s} id={`heat-${s}`}>
          <stop offset="0%" stopColor={LINE_STATE_INK[s]} stopOpacity={0.62} />
          <stop offset="28%" stopColor={LINE_STATE_COLORS[s]} stopOpacity={0.72} />
          <stop offset="62%" stopColor={LINE_STATE_COLORS[s]} stopOpacity={0.34} />
          <stop offset="100%" stopColor={LINE_STATE_COLORS[s]} stopOpacity={0} />
        </radialGradient>
      ))}
      {/* Retícula métrica: paso "redondo" recalculado con el zoom, para que
          siempre den la escala sin llegar a texturizar el suelo. */}
      {/* El trazo va en METROS (1 px × mpp) y no con `non-scaling-stroke`.
          Parece lo mismo —`step` ya sale de `mpp`, así que la retícula sigue
          midiendo un píxel en pantalla— pero `non-scaling-stroke` ata el grosor
          a la matriz de la vista, y con eso el navegador NO puede guardar la
          baldosa del patrón como mapa de bits: tiene que redibujar la retícula
          entera desde los vectores en cada fotograma del arrastre. Dentro de un
          `<pattern>` es el caso patológico, porque la baldosa se repite miles
          de veces. */}
      <pattern id="soil-grid" width={step} height={step} patternUnits="userSpaceOnUse">
        <path
          d={`M0,0 H${step} M0,0 V${step}`}
          fill="none"
          stroke={SOIL_GRID}
          strokeWidth={mpp}
          opacity={0.16}
        />
      </pattern>
    </defs>
  );
});

// ── Terreno ──────────────────────────────────────────────────────────────────
export const TerrainGround = memo(function TerrainGround({
  bounds,
}: {
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
}) {
  const w = Math.max(1, bounds.maxX - bounds.minX);
  const h = Math.max(1, bounds.maxY - bounds.minY);
  const pad = Math.max(w, h) * 0.6;
  const r = { x: bounds.minX - pad, y: bounds.minY - pad, w: w + 2 * pad, h: h + 2 * pad };
  return (
    <g style={{ pointerEvents: "none" }}>
      <rect x={r.x} y={r.y} width={r.w} height={r.h} fill={SOIL} />
      <rect x={r.x} y={r.y} width={r.w} height={r.h} fill="url(#soil-grid)" />
    </g>
  );
});

// ── Suelo de la nave (la figura sobre el terreno) ────────────────────────────
/**
 * La sombra que despega la nave del campo, SIN filtro.
 *
 * Era un `feDropShadow`, y un filtro SVG es lo más caro que se puede poner en
 * un mapa que se arrastra: obliga al navegador a pintar el elemento en un búfer
 * aparte y pasarle un desenfoque gaussiano. Como arrastrar reescribe el
 * `viewBox`, y eso invalida el dibujo ENTERO, se pagaban ocho desenfoques por
 * fotograma —uno por nave— al triple de densidad de un iPhone.
 *
 * Se sustituye por el mismo perímetro desplazado hacia abajo y contorneado dos
 * veces: un trazo ancho y muy flojo que hace de penumbra, y otro estrecho y algo
 * más firme pegado al borde. No es un desenfoque de verdad —la caída es en dos
 * escalones en vez de continua—, pero son dos trazos planos que el compositor
 * dibuja gratis y a tamaño de pantalla la diferencia no se ve.
 *
 * Va DENTRO del grupo girado, igual que el filtro de antes: la sombra cae hacia
 * el «abajo» de la nave, y al girarla gira con ella.
 */
const SHADOW_INK = "hsl(26 40% 20%)";
/** Un pasillo es suelo pisado: tierra clara, no un color de aviso. */
const PASILLO_FILL = "hsl(32 24% 80%)";
const PASILLO_EDGE = "hsl(32 22% 62%)";

const GreenhouseFloor = memo(function GreenhouseFloor({ gh, mpp }: { gh: GhModel; mpp: number }) {
  const d = pathOf(gh.perimeter);
  return (
    <g>
      <g transform={`translate(0, ${2.5 * mpp})`} style={{ pointerEvents: "none" }}>
        <path
          d={d}
          fill={SHADOW_INK}
          fillOpacity={0.13}
          stroke={SHADOW_INK}
          strokeOpacity={0.07}
          strokeWidth={7 * mpp}
          strokeLinejoin="round"
        />
        <path
          d={d}
          fill={SHADOW_INK}
          fillOpacity={0.12}
          stroke={SHADOW_INK}
          strokeOpacity={0.11}
          strokeWidth={3 * mpp}
          strokeLinejoin="round"
        />
      </g>
      <path d={d} fill={GH_FLOOR} />
      <path
        d={d}
        fill="none"
        stroke={GH_EDGE}
        strokeWidth={2}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );
});

// ── Líneas de cultivo: contexto, no dato ─────────────────────────────────────
/**
 * Las líneas de cultivo. NUNCA desaparecen del todo.
 *
 * Antes se desvanecían hasta cero al alejarse y la nave quedaba en blanco: en
 * el encuadre por defecto —que es el que ve todo el mundo al entrar— no había
 * ni rastro de las 335 líneas que anuncia la cabecera, así que la nave parecía
 * un solar vacío. Se hacía para evitar el moaré de cientos de líneas a dos
 * píxeles una de otra, y ese riesgo es real, pero la respuesta no es borrarlas:
 * es bajarles la tinta y afinar el trazo hasta que se lean como TEXTURA. De
 * lejos se ve que ahí hay líneas; al acercarse se ven las líneas.
 */
const ROWS_MIN_OPACITY = 0.34;
/**
 * Geometría de la trama de un sector: hacia dónde van sus líneas, cada cuánto y
 * dónde empieza la primera. Se calcula UNA vez por nave (atada a `gh.rows`), no
 * por fotograma.
 *
 * El paso es (recorrido total) / (nº de líneas − 1), NUNCA la separación
 * mediana: con 375 líneas en un sector, un error de milímetros en la mediana se
 * acumula en más de un metro de desfase en el otro extremo.
 *
 * Y la trama es POR SECTOR, no por nave: una misma nave mezcla sectores a 1,50 m
 * con otros a 2,01 m.
 */
interface BlockStripes {
  blockId: string;
  /** Separación real entre líneas, en metros. */
  pitch: number;
  count: number;
}

function stripesOf(rows: GhRow[]): BlockStripes | null {
  if (rows.length < 2) return null;
  // La línea MÁS LARGA da el ángulo: en una corta, el mismo error de extremo
  // pesa mucho más.
  let longest = rows[0];
  let bestLen = -1;
  for (const r of rows) {
    const len = Math.hypot(r.b.x - r.a.x, r.b.y - r.a.y);
    if (len > bestLen) { bestLen = len; longest = r; }
  }
  if (bestLen <= 0) return null;
  const ux = (longest.b.x - longest.a.x) / bestLen;
  const uy = (longest.b.y - longest.a.y) / bestLen;
  const nx = -uy;
  const ny = ux;

  let sMin = Infinity, sMax = -Infinity;
  for (const r of rows) {
    const s = r.a.x * nx + r.a.y * ny;
    if (s < sMin) sMin = s;
    if (s > sMax) sMax = s;
  }
  const pitch = (sMax - sMin) / (rows.length - 1);
  if (!Number.isFinite(pitch) || pitch <= 0) return null;

  return { blockId: rows[0].blockId, pitch, count: rows.length };
}

/** Por debajo de este paso EN PANTALLA, la trama se aplana a un tinte. */
const PITCH_FADE_PX = 2.4;
/** Por encima, rayas a plena fuerza. */
const PITCH_FULL_PX = 3.75;

/**
 * LAS LÍNEAS DE CULTIVO. Todas las de un sector, en UN camino.
 *
 * Esta capa ha pasado por tres formas y las tres tenían razón en algo:
 *
 *  1. UN NODO POR LÍNEA (hasta agosto). 805 elementos con extremos redondeados
 *     y `non-scaling-stroke`. Ese `vectorEffect` ata el grosor a la matriz de la
 *     vista, así que el navegador tiene que REHACER EL CONTORNO de cada línea en
 *     cada fotograma. Ochocientos nodos y ochocientos contornos.
 *  2. UN `<pattern>` POR SECTOR (agosto). Arregla lo de los nodos: un relleno
 *     por sector. Pero la baldosa de un patrón es un búfer que el rasterizador
 *     REHACE CADA VEZ QUE CAMBIA LA ESCALA, y aquí la baldosa mide todo el
 *     largo de la línea: a 20 px/m, un sector de 100 m pide una baldosa de
 *     2.000 px de ancho. Ampliando, ese búfer crece con el zoom, uno por sector
 *     y por fotograma. En Chrome de Android ni siquiera va por GPU. Eso es lo
 *     que se sentía como «al acercarme al mapa se arrastra» (29-08-2026).
 *  3. UN CAMINO POR SECTOR (ahora). Los 188 segmentos van en el mismo atributo
 *     `d`, con el grosor EN METROS. Un nodo por sector —lo bueno de (2)— y ni
 *     contornos que rehacer ni baldosas que regenerar: ampliar es transformar
 *     una geometría que ya está construida. Y el grosor en metros además es lo
 *     correcto: una línea de cultivo tiene un ancho real, son plantas.
 *
 * Lo que se conserva de (2) es el UMBRAL: hay una distancia a partir de la cual
 * las rayas no se distinguen y se pinta el tinte que producen al fundirse.
 */
/**
 * CÓMO SE PINTA LA TRAMA A ESTA DISTANCIA: con rayas, o con el tinte que las
 * rayas producen al fundirse.
 *
 * Hay una distancia a partir de la cual esas rayas NO SE VEN: con el paso por
 * debajo de un píxel y pico, la raya y su hueco caen dentro del mismo píxel y lo
 * que queda en pantalla es una mancha plana. Ahí el patrón se está pagando para
 * dibujar algo que nadie puede distinguir de un relleno liso. Justo esa
 * distancia —el invernadero entero en pantalla— es donde más se arrastra y se
 * pellizca.
 *
 * Así que por debajo del umbral se pinta el relleno liso, con la opacidad que
 * TENÍA la trama: raya por hueco, que es la fracción de píxel que la raya
 * cubría. Con eso el cambio no se ve — lo fija `cropTexture.test.ts`, que
 * comprueba que las dos fórmulas coinciden justo en la frontera.
 */
const PITCH_FLAT_PX = 1.4;

export function cropStripePaint(
  pitch: number,
  mpp: number,
): { flat: boolean; strokeW: number; opacity: number } {
  const pitchPx = pitch / mpp;
  const t = clamp01((pitchPx - PITCH_FADE_PX) / (PITCH_FULL_PX - PITCH_FADE_PX));
  const strokeW = Math.min(pitch * 0.55, 0.8 * mpp + 0.3 * mpp * t);
  const opacity = ROWS_MIN_OPACITY + (0.9 - ROWS_MIN_OPACITY) * t;
  if (pitchPx > PITCH_FLAT_PX) return { flat: false, strokeW, opacity };
  /* Lo que se veía: la raya cubre `strokeW / pitch` de cada paso, y el resto es
     hueco. Multiplicado por su opacidad, es el gris que salía al fundirse. */
  return { flat: true, strokeW, opacity: opacity * clamp01(strokeW / pitch) };
}

const CropTexture = memo(function CropTexture({ gh, mpp }: { gh: GhModel; mpp: number }) {
  const stripes = useMemo(() => {
    const byBlock = new Map<string, GhRow[]>();
    for (const r of gh.rows) {
      const list = byBlock.get(r.blockId);
      if (list) list.push(r);
      else byBlock.set(r.blockId, [r]);
    }
    /* El contorno del sector se sigue necesitando: es lo que se rellena cuando
       las rayas se funden. Un sector con líneas pero sin polígono se cae aquí. */
    const outline = new Map(gh.blocks.map((b) => [b.id, pathOf(b.points)]));
    const out: { blockId: string; pitch: number; outline: string; lines: string }[] = [];
    for (const rows of byBlock.values()) {
      const st = stripesOf(rows);
      const d = st ? outline.get(st.blockId) : undefined;
      if (!st || !d) continue;
      /* TODAS las líneas del sector EN UN SOLO `d`. Dos decimales —un
         centímetro— porque la cadena se parsea entera cada vez que cambia, y
         las coordenadas en crudo la triplican sin que se vea la diferencia. */
      let lines = "";
      for (const r of rows) {
        /* TODOS los trozos de la fila, no solo el primero. Una fila que una
           muesca cóncava o un pasillo parte en dos llega con `facetas`; pintar
           solo `a`/`b` dejaba media nave sin líneas (usuario 12-09-2026). */
        const trozos = r.facetas ?? [{ a: r.a, b: r.b }];
        for (const t of trozos) {
          lines += `M${t.a.x.toFixed(2)},${t.a.y.toFixed(2)}L${t.b.x.toFixed(2)},${t.b.y.toFixed(2)}`;
        }
      }
      out.push({ blockId: st.blockId, pitch: st.pitch, outline: d, lines });
    }
    return out;
  }, [gh.rows, gh.blocks]);

  if (stripes.length === 0) return null;

  return (
    <g style={{ pointerEvents: "none" }}>
      {stripes.map((st) => {
        const paint = cropStripePaint(st.pitch, mpp);
        /* Fundidas: un relleno liso. Ni geometría que recorrer ni nada que
           rehacer por fotograma, y se ve igual — la opacidad es la cobertura
           que tenían las rayas. */
        return paint.flat ? (
          <path key={st.blockId} d={st.outline} fill={ROW_GREEN} opacity={paint.opacity} />
        ) : (
          <path
            key={st.blockId}
            d={st.lines}
            fill="none"
            stroke={ROW_GREEN}
            /* EN METROS, no en píxeles de pantalla: el trazo escala con el
               plano, así que ampliar es una transformación y no un contorno
               nuevo. `non-scaling-stroke` aquí sería volver al fallo de agosto
               —rehacer el contorno de cada línea en cada fotograma—, y además
               las líneas de cultivo TIENEN un grosor real: son plantas. */
            strokeWidth={paint.strokeW}
            opacity={paint.opacity}
          />
        );
      })}
    </g>
  );
});

/**
 * Las líneas que SÍ tienen algo que contar: las que llevan un parte de plaga.
 * En toda la base de datos son CINCO, de 2.184 dibujables. Se pintan con la
 * tinta de su estado, porque ahora son un DATO y no el contexto que eran.
 */
const ReportedRows = memo(function ReportedRows({
  gh,
  model,
  mpp,
}: {
  gh: GhModel;
  model: FarmHealthModel;
  mpp: number;
}) {
  const reported = useMemo(
    () => gh.rows.filter((r) => model.lineHealth.has(r.id)),
    [gh.rows, model.lineHealth],
  );
  if (reported.length === 0 || mpp > LOD_ROWS_FADE) return null;
  return (
    <g style={{ pointerEvents: "none" }}>
      {reported.map((r) => {
        const health = model.lineHealth.get(r.id)!;
        return (
          <path
            key={r.id}
            d={`M${r.a.x},${r.a.y}L${r.b.x},${r.b.y}`}
            fill="none"
            stroke={LINE_STATE_INK[health.state]}
            strokeWidth={2.25}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            /* «Resuelto» se apaga en vez de desaparecer: su chincheta sigue
               ahí, y una chincheta sobre una línea que no dibujamos sería la
               siguiente incoherencia que reportar. */
            opacity={health.state === "resolved" ? 0.45 : 0.95}
          />
        );
      })}
    </g>
  );
});

// ── Bloques ──────────────────────────────────────────────────────────────────
/** Bandas alternas: los bloques se leen como ÁREAS. Una línea discontinua de
 *  1px entre cientos de líneas de cultivo se camufla; una banda no. El salto es
 *  de 1.08:1 a propósito — suficiente para separar, invisible para el calor. */
/**
 * PASILLOS: el hueco donde no hay planta.
 *
 * Estaban pintados en `GreenhouseFloor` y no se veían, que es lo que se reportó
 * («¿dónde están los pasillos?»). El motivo es de orden de pintado y no de
 * datos: un pasillo NO parte el sector —ese es justo su contrato—, así que el
 * polígono del sector cubre también el pasillo, y encima va `CropTexture`, que
 * raya el sector ENTERO. Debajo de eso no hay pasillo que se vea: las rayas de
 * cultivo le pasaban por encima como si allí hubiera plantas.
 *
 * Así que va después: sobre el relleno del sector y sobre la trama de cultivo.
 * Entonces las filas SE VEN terminar en su borde, que es lo que pasa de verdad.
 *
 * También por encima del calor de plaga: en un pasillo no hay planta, así que no
 * puede haber presión. Teñirlo sería afirmar algo falso.
 *
 * SE DIBUJA EN TODAS LAS CAPAS, analítica incluida (petición del usuario
 * 01-09-2026). Allí no hay líneas de cultivo —una textura verde encima
 * corrompería el tinte de gasto—, pero un pasillo no es textura: es la forma de
 * la nave, y la forma no cambia según la pestaña que se mire. Va por debajo de
 * `AnalyticsSelection`, así que el sector elegido se sigue distinguiendo.
 */
const GreenhousePasillos = memo(function GreenhousePasillos({ gh, mpp }: { gh: GhModel; mpp: number }) {
  if (gh.pasillos.length === 0) return null;
  return (
    /* Sin `pointerEvents`: el acierto sigue siendo del sector de alrededor —
       tocar un pasillo no puede ser tocar «nada». */
    <g style={{ pointerEvents: "none" }}>
      {gh.pasillos.map((ring, i) => (
        <path
          key={`pas-${i}`}
          d={pathOf(ring)}
          fill={PASILLO_FILL}
          stroke={PASILLO_EDGE}
          /* En metros y no `non-scaling-stroke`: los pasillos se repiten —varios
             por nave, por ocho naves— y ese vectorEffect está reservado a las
             marcas que van por decenas. Misma regla que los 38 contornos de
             sector; lo vigila `mapRenderCost.guard`. */
          strokeWidth={1 * mpp}
          strokeLinejoin="round"
        />
      ))}
    </g>
  );
});

const BlockFills = memo(function BlockFills({
  gh,
  model,
}: {
  gh: GhModel;
  model: FarmHealthModel;
}) {
  return (
    <g style={{ pointerEvents: "none" }}>
      {gh.blocks.map((b, i) =>
        i % 2 === 1 ? <path key={b.id} d={pathOf(b.points)} fill={BLOCK_TINT} /> : null,
      )}
      {/* El verde va DESPUÉS de la banda alterna —la tapa— y ANTES del calor y
          de las chinchetas, que se dibujan encima. Ese orden es lo que hace que
          un foco rojo sobre un sector tratado se siga leyendo rojo. */}
      {gh.blocks.map((b) => {
        const d = model.blockHealth.get(b.id)?.treatedDaysAgo;
        if (d == null) return null;
        const t = Math.min(1, Math.max(0, d / TREATED_BLOCK_DAYS));
        return (
          <path
            key={`tr-${b.id}`}
            d={pathOf(b.points)}
            fill={TREATED_BLOCK_TINT}
            opacity={TREATED_TINT_MIN + (TREATED_TINT_MAX - TREATED_TINT_MIN) * (1 - t)}
          />
        );
      })}
    </g>
  );
});

// ── Capa «Analítica»: el sector teñido por actividad ─────────────────────────
/**
 * Rampa de un solo tono sobre el suelo de la nave. Se para en 64 % de
 * luminosidad a propósito: por debajo, la tinta de `BlockLabels` deja de
 * leerse encima y habría que rehacer las etiquetas. Con este techo el
 * contraste se mantiene en ~5,4:1 en el paso más oscuro.
 *
 * El paso 0 es el propio suelo: «aquí no ha pasado nada» tiene que leerse como
 * suelo llano, no como un azul pálido más.
 */
export const ANALYTICS_RAMP_TREATMENTS = [
  GH_FLOOR,
  "hsl(207 55% 92%)",
  "hsl(208 60% 85%)",
  "hsl(209 62% 78%)",
  "hsl(210 64% 71%)",
  "hsl(211 66% 64%)",
] as const;

/**
 * GASTO en rojo (decisión del usuario 25-08-2026). Empieza en arena cálida y no
 * en rosa pálido: esta app tiene entrenado que el rojo es «brote», así que el
 * extremo bajo tiene que leerse «cálido, un poco» y solo el último escalón es
 * rojo sin ambigüedad. La luminosidad va 94/88/82/75/69 y no 92/85/78/71/64
 * como la azul, porque un rojo a la misma luminosidad HSL se percibe más
 * oscuro; copiar los números de la azul daba una rampa demasiado pesada arriba.
 *
 * Contraste con la tinta de las etiquetas (#143148): 12,03 / 10,33 / 8,53 /
 * 6,59 / 5,10 : 1. El escalón más oscuro MEJORA el azul que había (4,99:1).
 *
 * El recuento de TRATAMIENTOS se queda en frío a propósito: rojo para «mucho
 * dinero» se lee bien, pero rojo para «muchos tratamientos» se lee como
 * «muchos problemas», que es otra afirmación y puede ser falsa —una nave bien
 * llevada con un programa preventivo fuerte se encendería entera—.
 */
export const ANALYTICS_RAMP_SPEND = [
  GH_FLOOR,
  "hsl(30 86% 94%)",
  "hsl(24 86% 88%)",
  "hsl(18 84% 82%)",
  "hsl(12 80% 75%)",
  "hsl(6 74% 69%)",
] as const;

/** Compatibilidad: la rampa por defecto sigue siendo la de tratamientos. */
export const ANALYTICS_RAMP = ANALYTICS_RAMP_TREATMENTS;

/** Intensidad 0..1 → escalón de la rampa. 0 exacto se queda en el suelo. */
export function analyticsRampStep(intensity: number): number {
  if (!Number.isFinite(intensity) || intensity <= 0) return 0;
  const step = Math.ceil(intensity * (ANALYTICS_RAMP.length - 1));
  return Math.min(ANALYTICS_RAMP.length - 1, Math.max(1, step));
}

export interface AnalyticsPaint {
  /** blockId → 0..1, relativo al sector más activo del encuadre. */
  intensity: Map<string, number>;
  /** Rojo para el gasto, frío para el recuento de tratamientos. */
  ramp: readonly string[];
  selectedBlockId: string | null;
  selectedGreenhouseId: string | null;
}

const AnalyticsFills = memo(function AnalyticsFills({
  gh,
  paint,
}: {
  gh: GhModel;
  paint: AnalyticsPaint;
}) {
  return (
    <g style={{ pointerEvents: "none" }}>
      {gh.blocks.map((b) => (
        <path
          key={b.id}
          d={pathOf(b.points)}
          fill={paint.ramp[analyticsRampStep(paint.intensity.get(b.id) ?? 0)]}
        />
      ))}
    </g>
  );
});

/**
 * El aro de selección. Va en su propio grupo al final para no tocar el orden
 * de pintado que ya existe, y con `non-scaling-stroke` para que el grosor sea
 * constante en pantalla a cualquier zoom.
 */
const AnalyticsSelection = memo(function AnalyticsSelection({
  gh,
  paint,
}: {
  gh: GhModel;
  paint: AnalyticsPaint;
}) {
  const block = paint.selectedBlockId
    ? gh.blocks.find((b) => b.id === paint.selectedBlockId)
    : null;
  const points = block
    ? block.points
    : paint.selectedGreenhouseId === gh.id && !paint.selectedBlockId
      ? gh.perimeter
      : null;
  if (!points || points.length < 3) return null;
  const d = pathOf(points);
  return (
    <g style={{ pointerEvents: "none" }}>
      <path d={d} fill="none" stroke="#fff" strokeWidth={4.5} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      <path d={d} fill="none" stroke={INK} strokeWidth={2.25} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
    </g>
  );
});

/** Separación entre bloques: sólida y más oscura y fría que la línea de
 *  cultivo (4.54:1 sobre el suelo, 1.43:1 contra el verde), para que se lea
 *  como estructura y no como una línea más. */
const BlockEdges = memo(function BlockEdges({ gh, mpp }: { gh: GhModel; mpp: number }) {
  if (mpp > LOD_BLOCK_EDGES) return null;
  return (
    <g style={{ pointerEvents: "none" }}>
      {gh.blocks.map((b) => (
        <path
          key={b.id}
          d={pathOf(b.points)}
          fill="none"
          stroke={BLOCK_EDGE}
          /* En metros (1,25 px × mpp) y no `non-scaling-stroke`: son 38
             contornos en la finca de demostración, y atar su grosor a la matriz
             de la vista impide cachearlos. Al pellizcar engordan con el gesto y
             se recolocan al soltar, igual que ya hacen los nombres y los pines,
             que también salen de `mpp`. */
          strokeWidth={1.25 * mpp}
          strokeLinejoin="round"
          opacity={0.9}
        />
      ))}
    </g>
  );
});

// ── CALOR ────────────────────────────────────────────────────────────────────
/**
 * Mapa de calor de verdad: una MANCHA radial por foco, recortada al perímetro
 * de la nave. El tono es el estado y el tamaño/intensidad salen de `focusWeight`
 * (severidad × nº de reportes), la misma función pura y testeada que usaba el
 * cálculo por tramos.
 *
 * Historia de esta capa, porque importa para no repetirla:
 *  - v1 pintaba tramos de línea desenfocados a doble opacidad. El rojo de un
 *    brote grave llegaba al ojo a 1.60:1 en su presión mínima: invisible justo
 *    en el caso más decisivo, el foco nuevo y aislado.
 *  - v2 (2026-08-07) lo "arregló" volviendo los tramos nítidos y opacos. Ganó
 *    el contraste y perdió el mapa: parecían las líneas de cultivo subrayadas
 *    con rotulador (QA usuario: "una completa basura").
 *  - v3, esto: la presión es una zona, no una línea. Manchas que se solapan y
 *    se refuerzan, con núcleo caliente y borde que se disuelve. Van POR ENCIMA
 *    de las líneas, así que las tiñen sin taparlas ni subrayarlas.
 */
export const HeatField = memo(function HeatField({
  gh,
  model,
}: {
  gh: GhModel;
  model: FarmHealthModel;
}) {
  // Una mancha NO cruza de sector. Es la misma regla que `computeHeatSegments`
  // aplica al filtrar por `blockId`, y por el mismo motivo: un sector teñido de
  // más es un sector que se trata de más. Cada foco se recorta a SU bloque; el
  // que no tenga bloque conocido, al perímetro de la nave.
  const blockPts = new Map(gh.blocks.map((b) => [b.id, b.points]));
  const byBlock = new Map<string, typeof model.foci>();
  for (const f of model.foci) {
    if (f.ghId !== gh.id || f.state === "no_data") continue;
    const key = blockPts.has(f.blockId) ? f.blockId : "";
    const list = byBlock.get(key);
    if (list) list.push(f);
    else byBlock.set(key, [f]);
  }
  if (byBlock.size === 0) return null;

  /* UNA MANCHA POR SITIO, NO POR PARTE — y esto hay que hacerlo a mano.
     Desde que cada parte es su propio foco (31-08-2026), tres partes del mismo
     sector sin línea son tres focos en el MISMO punto. Pintar una mancha por
     foco las superpone y el alfa se compone: una mancha media sola va a 0,82,
     y tres iguales encima dan 1-(1-0,82)³ ≈ 0,99. O sea, opaca — se come la
     foto de satélite justo donde más hace falta verla, y con las pilas de
     siete de producción sería un tapón sólido.
     Se agrupan por ancla y sale UNA mancha: el estado más urgente del grupo, y
     el peso con el mismo factor logarítmico por número de partes que llevaba
     `focusWeight` cuando un foco ERA el grupo. El resultado es idéntico a lo
     que se veía antes de partir los focos, que es lo que se quería. */
  const blob = (fs: typeof model.foci) => {
    const head = fs[0];
    let state = fs[0].state;
    let sev = 0;
    for (const f of fs) {
      state = worseState(state, f.state);
      sev = Math.max(sev, focusWeight(f));
    }
    const w = Math.min(1, sev * Math.min(1.6, 1 + 0.25 * Math.log(fs.length)));
    // El kernel del modelo es σ=8m; la mancha visible llega a ~2.5σ y crece
    // con el peso del grupo.
    return (
      <circle
        key={head.id}
        cx={head.x}
        cy={head.y}
        r={14 + 16 * w}
        fill={`url(#heat-${state})`}
        opacity={(state === "resolved" ? 0.42 : 1) * (0.55 + 0.45 * w)}
      />
    );
  };

  /* Por ANCLA, redondeada al decímetro: los partes que comparten sitio lo
     comparten exacto —centroide del sector o punto medio de la línea—, así que
     no hace falta agrupar por cercanía como en la baraja. En METROS y no en
     píxeles a propósito: la mancha se mide en metros y no debe cambiar de forma
     con el zoom. */
  const atSamePoint = (fs: typeof model.foci) => {
    const by = new Map<string, typeof model.foci>();
    for (const f of fs) {
      const k = `${f.x.toFixed(1)}:${f.y.toFixed(1)}`;
      const arr = by.get(k);
      if (arr) arr.push(f);
      else by.set(k, [f]);
    }
    return [...by.values()];
  };

  return (
    <g style={{ pointerEvents: "none" }}>
      {/* Los recortes se definen AQUÍ, que es el único sitio que los usa, y solo
          los que de verdad recortan algo.

          Estaban en `GreenhouseGroup`, que emitía uno por sector SIEMPRE: 46 en
          la finca de demostración, con su polígono entero, rehechos en cada
          render. De esos 46 se usaban los tres o cuatro que tienen un foco
          encima —el otro cliente que había, la trama de cultivo, dejó de
          recortar—. Los demás eran geometría que nadie leía. */}
      <defs>
        {[...byBlock.keys()].map((blockId) =>
          blockId ? (
            <clipPath key={blockId} id={`blkclip-${blockId}`}>
              <path d={pathOf(blockPts.get(blockId)!)} />
            </clipPath>
          ) : (
            <clipPath key="gh" id={`ghclip-${gh.id}`}>
              <path d={pathOf(gh.perimeter)} />
            </clipPath>
          ),
        )}
      </defs>
      {[...byBlock].map(([blockId, foci]) => (
        <g key={blockId || "gh"} clipPath={`url(#${blockId ? `blkclip-${blockId}` : `ghclip-${gh.id}`})`}>
          {atSamePoint(foci).map(blob)}
        </g>
      ))}
    </g>
  );
});

// ── Overlays por bloque: PHI (contorno + candado) + IRM (chip) ───────────────
export const BlockOverlays = memo(function BlockOverlays({
  gh,
  model,
  mpp,
}: {
  gh: GhModel;
  model: FarmHealthModel;
  mpp: number;
}) {
  return (
    <g style={{ pointerEvents: "none" }}>
      {gh.blocks.map((b) => {
        const bh = model.blockHealth.get(b.id);
        if (!bh) return null;
        // El chip IRM pide más sitio que el nombre: va bajo el centroide.
        const { h } = blockBox(b.points);
        const room = h / mpp >= 44;
        return (
          <g key={b.id}>
            {/* El plazo de seguridad era un rayado violeta sobre el interior del
                bloque: cuarta capa de color encima del calor. Ahora es un
                contorno, y el interior queda libre para la presión. */}
            {bh.phi && (
              <path
                d={pathOf(b.points)}
                fill="none"
                stroke={PHI_INK}
                strokeWidth={2}
                strokeDasharray="6 4"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                opacity={0.9}
              />
            )}
            {room && bh.irm && (
              <g transform={`translate(${b.centroid.x}, ${b.centroid.y + 16 * mpp})`}>
                <rect
                  x={-15 * mpp} y={-7 * mpp} width={30 * mpp} height={14 * mpp} rx={7 * mpp}
                  fill={IRM_INK[bh.irm.status]}
                />
                <text
                  x={0} y={0.5 * mpp}
                  fontSize={9.5 * mpp} fontWeight={800} letterSpacing={0.6 * mpp}
                  fill="white" textAnchor="middle" dominantBaseline="middle"
                >
                  IRM
                </text>
              </g>
            )}
          </g>
        );
      })}
    </g>
  );
});

/** Nombres de bloque: 11px constantes en pantalla, tinta plena (13.2:1) y halo
 *  del color del suelo para sobrevivir encima de las líneas y del calor.
 *
 *  El gate es POR BLOQUE, no por zoom: cada nombre se dibuja solo si su bloque
 *  mide de verdad lo suficiente en pantalla. Así en la vista de finca los
 *  bloques grandes ya se nombran y los recortes de dos líneas no se llenan de
 *  texto ilegible — cosa que un umbral global de zoom no puede distinguir. */
export const BlockLabels = memo(function BlockLabels({ gh, mpp }: { gh: GhModel; mpp: number }) {
  const labels = useMemo(() => {
    const named = gh.blocks.filter((b) => b.name?.trim()); // sin nombre en BD: no pintes vacío
    if (named.length === 0) return [];
    // ¿Bandas apiladas (comparten carril vertical) o columnas (carril horizontal)?
    const xs = named.map((b) => b.centroid.x);
    const ys = named.map((b) => b.centroid.y);
    const stacked = Math.max(...ys) - Math.min(...ys) >= Math.max(...xs) - Math.min(...xs);
    const lane = median(stacked ? xs : ys);

    // "Bloque 1…5" dentro de una nave que ya se llama por su píldora es
    // repetir el sustantivo cinco veces. Si TODOS comparten primera palabra,
    // se cae: quedan "1…5", que caben siempre y se leen antes.
    const first = named[0].name.trim().split(/\s+/)[0];
    const dropPrefix =
      named.length > 1 &&
      first.length > 1 &&
      named.every((b) => {
        const parts = b.name.trim().split(/\s+/);
        return parts.length > 1 && parts[0] === first;
      });
    const display = (name: string) =>
      dropPrefix ? name.trim().split(/\s+/).slice(1).join(" ") : name.trim();

    const out: { id: string; x: number; y: number; text: string }[] = [];
    for (const b of named) {
      const bb = blockBox(b.points);
      // 11px de texto + halo caben en 15px. Más alto que eso dejaba sin nombre
      // las bandas estrechas y partía la serie (A, _, C, D…), que confunde más
      // que un nombre algo justo.
      if (bb.h / mpp < 15) continue;
      const full = display(b.name);
      // Se prueba entero y, si no cabe DENTRO del polígono, se acorta. Nunca se
      // saca fuera: antes se pierde el nombre que ensuciar el dibujo.
      let placed: { text: string; at: XY } | null = null;
      for (let n = full.length; n >= 1; n--) {
        const text = n === full.length ? full : `${full.slice(0, n)}…`;
        const halfW = (text.length * 6.2 * mpp) / 2;
        const at = placeBlockLabel(b.points, b.centroid, lane, stacked, halfW);
        if (at) { placed = { text, at }; break; }
      }
      if (!placed) continue;
      out.push({ id: b.id, x: placed.at.x, y: placed.at.y, text: placed.text });
    }
    return out;
  }, [gh.blocks, mpp]);

  if (labels.length === 0) return null;
  return (
    <g style={{ pointerEvents: "none" }}>
      {labels.map((l) => (
        <text
          key={`n-${l.id}`}
          x={l.x}
          y={l.y}
          /* Contragiro sobre su propio ancla: el nombre del sector se queda en
             su sitio dentro del polígono pero se lee en horizontal. Girado con
             la nave, a 90° queda tumbado y a 180° del revés. */
          transform={gh.rotationDeg ? `rotate(${-gh.rotationDeg} ${l.x} ${l.y})` : undefined}
          fontSize={11 * mpp}
          fontWeight={700}
          fill={INK}
          stroke={GH_FLOOR}
          strokeWidth={3.4 * mpp}
          paintOrder="stroke"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {l.text}
        </text>
      ))}
    </g>
  );
});

/**
 * EL TAMAÑO DEL PIN ES LA GRAVEDAD, desde que el color dejó de serlo.
 *
 * La gravedad estaba dentro de la paleta —rojo para alta y crítica, naranja
 * para el resto— y por eso el color no podía decir también en qué punto estaba
 * el parte. Sacarla al radio la deja legible a la vez que el estado y de un
 * solo vistazo: un punto gordo y rojo es «crítico y sin tocar».
 *
 * Solo se aplica a lo que TODAVÍA PIDE ALGO (`open`, `planned`). Un parte ya
 * tratado o cerrado se dibuja del mismo tamaño siempre: lo grave que fuera en
 * su día no cambia nada de lo que hay que hacer hoy, y dejarlo gordo lo único
 * que hace es robarle sitio a los que sí.
 */
const SEV_RADIUS: Record<string, number> = { critical: 12, high: 10.5, medium: 9.5, low: 8.5 };
const RADIUS_BASE = 9.5;
const NEEDS_ATTENTION: LineState[] = ["open", "planned"];

// ── Focos: pines constantes en pantalla ──────────────────────────────────────
export const FociMarkers = memo(function FociMarkers({
  gh,
  model,
  mpp,
}: {
  gh: GhModel;
  model: FarmHealthModel;
  mpp: number;
}) {
  /* `layoutFoci` ya devuelve las cartas en orden de pintado —las de atrás
     primero—, así que se recorre tal cual y el parte más reciente queda encima.
     Es LA MISMA función que usa el hit-test: si aquí se dibujara con otra
     cuenta, las chinchetas de atrás no responderían donde se ven. */
  const placed = useMemo(
    () => layoutFoci(model.foci.filter((f) => f.ghId === gh.id), mpp),
    [model.foci, gh.id, mpp],
  );
  return (
    <g style={{ pointerEvents: "none" }}>
      {placed.map(({ focus: f, x, y, index, size }) => {
        const urge = NEEDS_ATTENTION.includes(f.state);
        const r = (urge ? SEV_RADIUS[f.severity ?? ""] ?? RADIUS_BASE : RADIUS_BASE) * mpp;
        /* El anillo se queda para lo CRÍTICO y sin tratar. Con la gravedad ya
           en el radio es redundante a propósito: es el único caso en el que
           merece la pena decirlo dos veces. */
        const ring = f.state === "open" && f.severity === "critical";
        const count = f.count ?? 1;
        return (
          <g
            key={f.id}
            transform={`translate(${x}, ${y})`}
            opacity={focusOpacity(f)}
            /* Para las pruebas de navegador: la capa no recibe eventos (el toque
               se resuelve por coordenadas), así que una prueba localiza la
               chincheta por estos atributos y pulsa en el centro de su núcleo.
               `data-focus-state` y no `data-state`: ese nombre es de Radix. */
            data-testid="focus-pin"
            data-focus-id={f.id}
            data-focus-kind={f.kind ?? "implicit"}
            data-focus-state={f.state}
            data-focus-severity={f.severity ?? ""}
            data-focus-count={count}
            data-focus-evolution={f.evolution ?? ""}
            data-focus-rebound={f.rebound ? "true" : "false"}
            data-focus-approx={f.approx ? "true" : "false"}
            data-session-ids={f.sessionIds.join(",")}
          >
            {ring && (
              <circle r={r + 4 * mpp} fill="none" stroke={LINE_STATE_INK.open}
                strokeWidth={1.5} vectorEffect="non-scaling-stroke" opacity={0.45} />
            )}
            <circle
              data-pin-core
              r={r}
              fill={LINE_STATE_INK[f.state]}
              stroke="white"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
              strokeDasharray={f.approx ? "3 2.5" : undefined}
            />
            {/* EL NÚMERO SOLO EN LA CARTA DE DELANTE, y dice cuántas hay
                DETRÁS. Antes cada pin llevaba su recuento de partes fusionados;
                hoy un pin es un parte, así que un número dentro sería siempre
                un «1». Lo que sí hace falta saber de un vistazo es que la pila
                tiene fondo. */}
            {index === 0 && size > 1 && (
              <text
                x={0} y={0.5 * mpp}
                fontSize={10 * mpp} fontWeight={800}
                fill="white" textAnchor="middle" dominantBaseline="middle"
              >
                {size}
              </text>
            )}
            {/* CUÁNTOS MUESTREOS LLEVA EL FOCO, arriba a la derecha. Es otra cosa
                que el número de dentro: aquel cuenta chinchetas apiladas en el
                mismo sitio; este, las veces que alguien ha vuelto a ESTE foco.
                Solo relleno, sin trazo: el presupuesto de `non-scaling-stroke`
                del mapa no se toca. */}
            {count > 1 && (
              <g transform={`translate(${r * 0.78}, ${-r * 0.78})`}>
                <circle r={6.2 * mpp} fill="white" />
                <text
                  x={0} y={0.4 * mpp}
                  fontSize={8 * mpp} fontWeight={800}
                  fill="#1f2937" textAnchor="middle" dominantBaseline="middle"
                >
                  {count}
                </text>
              </g>
            )}
            {/* EL AVANCE frente al muestreo anterior, abajo a la derecha. En TINTA
                NEUTRA y con FORMA, nunca en rojo o verde: el color del mapa
                contesta una sola pregunta —en qué estado está— y el avance es
                otra. Como trazado y no como letra: «▲» sale distinto en cada
                WebView. */}
            {(f.evolution === "worse" || f.evolution === "same" || f.evolution === "better") && (
              <g transform={`translate(${r * 0.78}, ${r * 0.78})`}>
                <circle r={5.6 * mpp} fill="white" />
                <path
                  fill="#1f2937"
                  d={
                    f.evolution === "worse"
                      ? `M0 ${-3.2 * mpp} L${3.2 * mpp} ${2.4 * mpp} L${-3.2 * mpp} ${2.4 * mpp} Z`
                      : f.evolution === "better"
                        ? `M0 ${3.2 * mpp} L${3.2 * mpp} ${-2.4 * mpp} L${-3.2 * mpp} ${-2.4 * mpp} Z`
                        : `M${-3.2 * mpp} ${-1.1 * mpp} H${3.2 * mpp} V${1.1 * mpp} H${-3.2 * mpp} Z`
                  }
                />
              </g>
            )}
          </g>
        );
      })}
    </g>
  );
});

/**
 * LAS PULVERIZACIONES, en cuadrado y en verde.
 *
 * CUADRADO REDONDEADO y no círculo, a propósito. Un parte ya tratado también es
 * verde, así que con el color solo no se distinguirían: la forma es lo que dice
 * «esto no es un parte, es algo que se hizo». Círculo = lo que pasa, cuadrado =
 * lo que hicimos.
 *
 * Y DOS PESOS. La que explica un parte abierto —misma plaga, dentro de ventana—
 * va entera, porque es la que está aclarando ese rojo y quien mire querrá saber
 * por qué. Las de calendario van al 55 %: están para dar contexto, no para
 * competir con los partes.
 */
const TREATMENT_INK = LINE_STATE_INK.treated;

const TreatmentMarkers = memo(function TreatmentMarkers({
  gh,
  model,
  mpp,
}: {
  gh: GhModel;
  model: FarmHealthModel;
  mpp: number;
}) {
  /* `?? []` a propósito: `treatments` es nuevo en el modelo, y un modelo que
     venga sin él —una caché de react-query de antes del despliegue, un fixture
     de test— reventaría el mapa entero en vez de quedarse sin esta capa. */
  const placed = useMemo(
    () => layoutTreatments((model.treatments ?? []).filter((t) => t.ghId === gh.id), mpp),
    [model.treatments, gh.id, mpp],
  );
  if (placed.length === 0) return null;
  return (
    <g style={{ pointerEvents: "none" }}>
      {placed.map(({ treatment: t, x, y, index, size }) => {
        const half = 9 * mpp;
        return (
          <g key={t.id} transform={`translate(${x}, ${y})`} opacity={t.explains ? 1 : 0.55}>
            <rect
              x={-half} y={-half} width={half * 2} height={half * 2} rx={5.5 * mpp}
              fill={TREATMENT_INK}
              stroke="white"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            />
            {/* La boquilla: un chorro de tres gotas saliendo en diagonal. Se
                dibuja a mano porque un icono de librería dentro del SVG del
                mapa habría que escalarlo con `mpp` a mano igual, y encima
                traería su propio viewBox. */}
            <g fill="white" transform={`scale(${mpp})`}>
              <rect x={-5.5} y={-1} width={5} height={2} rx={1} />
              <rect x={-6.5} y={-3.5} width={2} height={7} rx={1} />
              <circle cx={2.2} cy={-3.2} r={1.15} />
              <circle cx={3.6} cy={0} r={1.15} />
              <circle cx={2.2} cy={3.2} r={1.15} />
            </g>
            {index === 0 && size > 1 && (
              <text
                x={0} y={half + 7 * mpp}
                fontSize={9.5 * mpp} fontWeight={800}
                fill={TREATMENT_INK} textAnchor="middle" dominantBaseline="middle"
              >
                {size}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
});

/** Nombre de la nave como píldora JUSTO ENCIMA del plano y centrada sobre él:
 *  constante en pantalla y recortada, para que un nombre largo no invada la nave
 *  vecina.
 *
 *  Antes se clavaba en la esquina de arriba a la izquierda de la caja envolvente.
 *  En una nave recta eso cae dentro y se lee bien, pero las naves reales están
 *  giradas: esa esquina es hueco, así que la píldora salía flotando fuera del
 *  dibujo (Nave 4) o encima de las líneas del plano (Nave 5). Puesta fuera y
 *  centrada no puede taparlas — el encuadre reserva un 6 % de margen alrededor,
 *  así que arriba hay sitio. */
/**
 * Dónde cae la píldora del nombre, en coordenadas LOCALES de la nave.
 *
 * La usan el dibujo y el toque, y por eso vive aquí y no dentro del componente:
 * en la capa «Analítica» tocar el nombre elige la NAVE ENTERA, que es la única
 * forma de pedir sus números —los sectores se comen todo el interior, así que
 * dentro del polígono no queda un solo punto que signifique «la nave»—. Si el
 * rectángulo que se pinta y el que se toca se calcularan por separado, un día
 * dejarían de coincidir y el nombre respondería un poco más allá de donde está.
 */
export function greenhouseLabelRect(gh: GhModel, mpp: number) {
  const name = gh.name.length > 22 ? `${gh.name.slice(0, 21)}…` : gh.name;
  const padX = 8 * mpp;
  const h = 20 * mpp;
  const w = name.length * 6.6 * mpp + padX * 2;
  const box = gh.rotationDeg
    ? rotatedPlaneBox({ offset: { x: 0, y: 0 }, rotationDeg: gh.rotationDeg, pivot: gh.pivot }, gh.perimeter)
    : gh.bbox;
  const cx = (box.minX + box.maxX) / 2;
  return { name, padX, h, w, x: cx - w / 2, y: box.minY - h - 6 * mpp };
}

/**
 * ¿El toque ha caído en el nombre de alguna nave? En coordenadas de MUNDO.
 *
 * Se prueba antes que nada porque la píldora vive FUERA del polígono, colgada
 * por encima, así que no compite con ningún sector.
 */
export function hitGreenhouseLabel(
  model: FarmHealthModel,
  world: XY,
  mpp: number,
): { ghId: string } | null {
  for (const gh of model.greenhouses) {
    const r = greenhouseLabelRect(gh, mpp);
    const lx = world.x - gh.offset.x;
    const ly = world.y - gh.offset.y;
    /* Un pelo de holgura vertical: la píldora mide 20 px de alto y el dedo no
       es un punto. */
    const padY = 4 * mpp;
    if (lx >= r.x && lx <= r.x + r.w && ly >= r.y - padY && ly <= r.y + r.h + padY) {
      return { ghId: gh.id };
    }
  }
  return null;
}

const GreenhouseLabel = memo(function GreenhouseLabel({ gh, mpp }: { gh: GhModel; mpp: number }) {
  const { name, padX, h, w } = greenhouseLabelRect(gh, mpp);
  /* La caja sale de `greenhouseLabelRect`, que ya tiene en cuenta la huella
     GIRADA: con la nave a 90° la caja sin girar dejaba la píldora flotando a un
     lado, encima de la vecina. */
  const r = greenhouseLabelRect(gh, mpp);
  return (
    <g
      transform={`translate(${r.x}, ${r.y})`}
      style={{ pointerEvents: "none" }}
    >
      <rect
        width={w} height={h} rx={h / 2}
        fill="white" fillOpacity={0.94}
        stroke={GH_EDGE} strokeWidth={1} vectorEffect="non-scaling-stroke" strokeOpacity={0.25}
      />
      <text
        x={w / 2} y={h / 2 + 0.5 * mpp}
        fontSize={11.5 * mpp} fontWeight={700}
        fill={INK} textAnchor="middle" dominantBaseline="middle"
      >
        {name}
      </text>
    </g>
  );
});

// ── Grupo completo de una nave ───────────────────────────────────────────────
export const GreenhouseGroup = memo(function GreenhouseGroup({
  gh,
  model,
  showFoci,
  mpp,
  analytics = null,
  pest = true,
}: {
  gh: GhModel;
  model: FarmHealthModel;
  showFoci: boolean;
  mpp: number;
  /**
   * Capa «Analítica». Cuando llega, TODA la lectura de plaga se apaga —calor,
   * pines de PHI y focos— y el sector se tiñe por actividad: son dos formas
   * distintas de mirar la misma finca, no dos capas superpuestas.
   */
  analytics?: AnalyticsPaint | null;
  /**
   * ¿Se lee la PLAGA encima del plano? En «Sensores» no: allí el mapa es el
   * plano a secas y los sensores encima (petición del usuario 31-08-2026).
   *
   * Iba implícito en `analytics`, y por eso fallaba: apagar la plaga solo
   * estaba escrito para Analítica, así que la capa de sensores heredaba el
   * calor, los tramos de línea a color y el borde morado del plazo de
   * seguridad. Se veía como una lectura de plaga con unos iconos de sensor
   * pegados encima — dos afirmaciones a la vez sobre la misma nave.
   *
   * Lo que NO apaga: el suelo, las bandas de sector, los bordes, los rótulos
   * y las líneas de cultivo. Eso es el PLANO, no el dato; quitarlo dejaría los
   * sensores flotando sobre una silueta vacía y sin forma de situarlos.
   */
  pest?: boolean;
}) {
  /* Dos grupos anidados, y el orden importa: primero se coloca la nave y
     DESPUÉS se gira sobre su propio centro. Al revés, girar la movería por la
     pantalla y el desplazamiento guardado dejaría de significar «dónde está».

     El giro es un `transform` y NO se hornea en la geometría: `perimeter`,
     `blocks[].points` y las líneas las escribe el asistente de invernaderos y
     las lee el kernel vendorizado. `hitTestModel` deshace exactamente esta
     misma cuenta con `toLocal`; si las dos se separan, tocar un sector devuelve
     el de al lado. */
  const rot = gh.rotationDeg;
  /* Analítica ya apagaba la plaga; ahora Sensores tambien. Una sola variable
     para que ninguna capa se quede a medio apagar la proxima vez. */
  const showPest = pest && !analytics;
  return (
    <g transform={`translate(${gh.offset.x}, ${gh.offset.y})`}>
      <g transform={rot ? `rotate(${rot}, ${gh.pivot.x}, ${gh.pivot.y})` : undefined}>
      {/* Estructura primero, calor encima tiñéndola, marcas al final. */}
      <GreenhouseFloor gh={gh} mpp={mpp} />
      {analytics ? <AnalyticsFills gh={gh} paint={analytics} /> : <BlockFills gh={gh} model={model} />}
      {/* En ANALÍTICA no se dibuja ni una línea de cultivo (petición del
          usuario). Además de que aquí el sector es la unidad, una textura verde
          encima del tinte de gasto corrompería justo el color que hay que leer. */}
      {/* La textura sustituye a las ~800 líneas; solo se dibujan de verdad las
          que llevan un parte de plaga. En ANALÍTICA no se dibuja ninguna de las
          dos: el sector es la unidad y un verde encima corrompería el tinte. */}
      {!analytics && <CropTexture gh={gh} mpp={mpp} />}
      {showPest && <HeatField gh={gh} model={model} />}
      {/* Después de la trama de cultivo Y del calor: un pasillo es un hueco sin
          planta, así que ni lo rayan las filas ni lo tiñe la presión de plaga.
          EN ANALÍTICA TAMBIÉN (petición del usuario 01-09-2026): aquí no se
          dibujan líneas de cultivo para no corromper el tinte de gasto, pero un
          pasillo no es textura, es la forma de la nave — y la forma no cambia
          según la pestaña que se mire. */}
      <GreenhousePasillos gh={gh} mpp={mpp} />
      <BlockEdges gh={gh} mpp={mpp} />
      {showPest && <BlockOverlays gh={gh} model={model} mpp={mpp} />}
      <BlockLabels gh={gh} mpp={mpp} />
      {showPest && <ReportedRows gh={gh} model={model} mpp={mpp} />}
      {/* Los tratamientos van DEBAJO de los partes en el orden de pintado: si
          se solapan, manda lo que está sin resolver. */}
      {showPest && showFoci && <TreatmentMarkers gh={gh} model={model} mpp={mpp} />}
      {showPest && showFoci && <FociMarkers gh={gh} model={model} mpp={mpp} />}
      {analytics && <AnalyticsSelection gh={gh} paint={analytics} />}
      </g>
      {/* FUERA del giro: la píldora del nombre se ancla arriba de la nave y se
          lee en horizontal siempre. Dentro del grupo girado saldría tumbada —a
          90° ilegible— y sobre el costado de la nave en vez de encima. */}
      <GreenhouseLabel gh={gh} mpp={mpp} />
    </g>
  );
});
